import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { 
  UserIcon, 
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { ConfirmModal } from '../components/ui/ConfirmModal';

export const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Carica dati completi del profilo
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => api.getProfile(),
    enabled: !!user,
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getDeviceIcon = (platform: string) => {
    if (platform?.toLowerCase().includes('mobile') || platform?.toLowerCase().includes('android') || platform?.toLowerCase().includes('ios')) {
      return DevicePhoneMobileIcon;
    }
    return ComputerDesktopIcon;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        {/* Pulsante Logout */}
        <button
          onClick={() => setShowLogoutModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
        >
          <ArrowRightOnRectangleIcon className="h-5 w-5" />
          <span>Logout</span>
        </button>
      </div>

      {/* User Info */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <UserIcon className="h-8 w-8 text-primary-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">
              {user?.firstName && user?.lastName 
                ? `${user.firstName} ${user.lastName}` 
                : user?.email}
            </h2>
            <p className="text-gray-600">{user?.email}</p>
            <p className="text-sm text-gray-500">
              Membro dal {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('it-IT') : 'N/A'}
            </p>
            {profileData?.activationCode && (
              <div className="mt-2 text-sm">
                <p className="text-gray-500">
                  Codice Attivazione: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{profileData.activationCode.code}</span>
                </p>
                <p className="text-gray-500">
                  Dispositivi: {profileData.activationCode.usedDevices}/{profileData.activationCode.maxDevices}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dispositivi Connessi */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Dispositivi Connessi</h3>
        {profileLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse flex items-center space-x-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : profileData?.devices && profileData.devices.length > 0 ? (
          <div className="space-y-3">
            {profileData.devices.map((device: any) => {
              const DeviceIcon = getDeviceIcon(device.platform);
              return (
                <div key={device.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <DeviceIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {device.deviceName || 'Dispositivo Sconosciuto'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {device.platform} • Connesso il {new Date(device.createdAt).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs ${
                    device.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {device.isActive ? 'Attivo' : 'Inattivo'}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <DevicePhoneMobileIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h4 className="mt-2 text-sm font-medium text-gray-900">Nessun dispositivo connesso</h4>
            <p className="mt-1 text-sm text-gray-500">
              I tuoi dispositivi connessi appariranno qui.
            </p>
          </div>
        )}
      </div>

      {/* Modal di conferma logout */}
      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        title="Conferma Logout"
        message="Sei sicuro di voler uscire dall'applicazione?"
        confirmText="Logout"
        cancelText="Annulla"
        type="warning"
      />
    </div>
  );
};
